import React, { useState } from "react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Phone, EnvelopeSimple, MapPin, Clock } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useBusinessSettings } from "@/context/SettingsContext";
import { pushGenerateLead } from "@/lib/analytics";
import { log } from "@/lib/logger";

export default function Contact() {
  const s = useBusinessSettings();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", city: "", state: "Nevada", site_type: "General inquiry", security_concern: "General inquiry", details: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, consent: true });
      try {
        await pushGenerateLead({
          email: form.email,
          phone: form.phone,
          state: form.state,
          city: form.city,
          industry: form.site_type,
          form_name: "Contact",
          value: 1,
        });
      } catch (analyticsErr) {
        log.error("analytics push failed:", analyticsErr);
      }
      toast.success("Message received. We'll be in touch shortly.");
      setForm({ ...form, first_name: "", last_name: "", email: "", phone: "", details: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again or call 702-929-0390.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Contact" }]} /></section>
      <section className="container-x pb-16">
        <div className="eyebrow text-solar mb-3">Contact</div>
        <h1 className="mb-4 max-w-3xl">Let's talk about your site.</h1>
        <p className="text-white/75 text-lg max-w-2xl">
          For a full site assessment and quote, use the <a href="/request-a-quote" className="text-solar underline">Request Assessment</a> page. For general questions, use the form below or call us directly.
        </p>
      </section>

      <section className="container-x pb-20 grid lg:grid-cols-[1fr_1.5fr] gap-10">
        <div className="border border-chrome bg-steel p-8 space-y-6">
          <div>
            <div className="eyebrow text-solar mb-2">Talk to us</div>
            <a href={`tel:${(s.phone || "").replace(/[^0-9+]/g,'')}`} data-testid="contact-phone-link" className="flex items-center gap-3 text-white text-xl font-display font-bold hover:text-solar transition-colors">
              <Phone size={22} weight="fill" className="text-solar" /> {s.phone}
            </a>
          </div>
          <div>
            <div className="eyebrow text-solar mb-2">Email</div>
            <a href={`mailto:${s.email}`} data-testid="contact-email-link" className="flex items-center gap-3 text-white hover:text-solar transition-colors">
              <EnvelopeSimple size={20} weight="fill" className="text-solar" /> {s.email}
            </a>
          </div>
          <div>
            <div className="eyebrow text-solar mb-2">Headquarters</div>
            <div className="flex items-center gap-3 text-white/85">
              <MapPin size={20} weight="fill" className="text-solar" /> {s.headquarters}
            </div>
          </div>
          <div>
            <div className="eyebrow text-solar mb-2">Hours</div>
            <div className="flex items-center gap-3 text-white/85">
              <Clock size={20} weight="fill" className="text-solar" /> {s.service_hours || "Monday–Friday, 8:00 AM – 6:00 PM (Pacific)"}
            </div>
          </div>
          <div className="pt-6 border-t border-chrome">
            <div className="eyebrow text-solar mb-3">Certifications</div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="border border-solar/40 text-solar px-2.5 py-1 uppercase tracking-wider font-bold">NDOT SBE</span>
              <span className="border border-solar/40 text-solar px-2.5 py-1 uppercase tracking-wider font-bold">NV CCTV Approved</span>
            </div>
          </div>
        </div>

        <form data-testid="contact-form" onSubmit={submit} className="border border-chrome bg-steel p-8 space-y-4">
          <input type="text" name="website" tabIndex="-1" autoComplete="off" style={{position:'absolute',left:'-9999px'}} value={form.website} onChange={set("website")} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="First name" value={form.first_name} onChange={set("first_name")} testid="input-first-name" required />
            <Field label="Last name" value={form.last_name} onChange={set("last_name")} testid="input-last-name" required />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Business email" type="email" value={form.email} onChange={set("email")} testid="input-email" required />
            <Field label="Phone" value={form.phone} onChange={set("phone")} testid="input-phone" required />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="City" value={form.city} onChange={set("city")} testid="input-city" required />
            <SelectField label="State" value={form.state} onChange={set("state")} testid="input-state" options={["Nevada","Utah","Other"]} />
          </div>
          <TextareaField label="How can we help?" value={form.details} onChange={set("details")} testid="input-details" rows={5} required />
          <button data-testid="contact-submit-btn" disabled={submitting} className="btn-solar w-full sm:w-auto disabled:opacity-60">
            {submitting ? "Sending…" : "Send Message"}
          </button>
        </form>
      </section>
    </div>
  );
}

export function Field({ label, testid, required, ...props }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest text-white/70 mb-1.5 font-bold">{label}{required && <span className="text-solar"> *</span>}</div>
      <input required={required} data-testid={testid} {...props} className="w-full bg-obsidian border border-chrome px-4 py-3 text-white text-sm focus:border-solar outline-none" />
    </label>
  );
}
export function SelectField({ label, testid, options = [], ...props }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest text-white/70 mb-1.5 font-bold">{label}</div>
      <select data-testid={testid} {...props} className="w-full bg-obsidian border border-chrome px-4 py-3 text-white text-sm focus:border-solar outline-none">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
export function TextareaField({ label, testid, required, ...props }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest text-white/70 mb-1.5 font-bold">{label}{required && <span className="text-solar"> *</span>}</div>
      <textarea required={required} data-testid={testid} {...props} className="w-full bg-obsidian border border-chrome px-4 py-3 text-white text-sm focus:border-solar outline-none resize-y" />
    </label>
  );
}
